import axios from "./axiosInstance";
import config from "./config";
import { captureException } from "../utils/sentry";

const BASE_URL = config.BASE_URL;

// Create a new athlete
export const createAthlete = (newForm) => {
  return axios.post(`${BASE_URL}/athletes`, newForm, {
    headers: {
      "Content-Type": "application/json",
    },
  });
};

// Create a new athlete
export const reassignAthletes = (token, newForm) => {
  return axios
    .post(`${BASE_URL}/athletes/reassign`, newForm, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {
      return err?.response?.data;
    });
};

// GET
export const getAthletesByMember = (token, id) => {
  return axios
    .get(`${BASE_URL}/athletes/member/${id}`, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {
      captureException(err);
      throw err;
    });
};

// GET
export const getAthleteById = (token, id) => {
  return axios
    .get(`${BASE_URL}/athletes/${id}`, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {
      return err?.response;
    });
};

// GET
/**
 * Names only, for the attendance sheet's "Unenrolled athletes" makeup picker.
 *
 * Gated on `mark_attendance`, not `manage_athletes`, so the roles that only
 * mark attendance (`attendance`, `junior_staff`) can actually see the list.
 * They could not before: this used getAllAthletes, whose route requires
 * manage_athletes, so the request 403'd and the section rendered an empty
 * table under its heading (Dan, 2026-09-10).
 *
 * Returns `{ id, first_name, last_name }` — no DOB, phone or medical fields.
 * Use getAthleteById for the detail view, which is permission-gated on its own.
 */
export const getAttendanceRoster = (
  token,
  { page = 1, limit = 20, searchTerm = "" }
) => {
  return axios
    .get(`${BASE_URL}/athletes/attendance-roster`, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
      params: { page, limit, searchTerm },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {
      captureException(err);
      throw err;
    });
};

// GET
export const getAllAthletes = (
  token,
  { page = 1, limit = 20, searchTerm = "" }
) => {
  return axios
    .get(`${BASE_URL}/athletes`, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
      params: { page, limit, searchTerm }, // ✅ send query params
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {
      captureException(err);
      throw err;
    });
};

// GET
/**
 * @param {{ status?: string, programs?: string[], page?: number, limit?: number,
 *   searchTerm?: string, excludeArchived?: boolean }} [filters]
 * @param {string | null | undefined} token
 */
export const getFilteredAthletes = (
  { status = "enrolled", programs = [], page = 1, limit = 20, searchTerm = "", excludeArchived = undefined } = {},
  token
) => {
  const params = new URLSearchParams();

  if (status) params.append("status", status);
  if (Array.isArray(programs) && programs.length > 0) {
    params.append("programs", programs.join(","));
  }
  if (page) params.append("page", page);
  if (limit) params.append("limit", limit);
  if (searchTerm) params.append("searchTerm", searchTerm);
  if (excludeArchived !== undefined) params.append("excludeArchived", excludeArchived);

  return axios
    .get(`${BASE_URL}/athletes/admin/athletes/filtered?${params.toString()}`, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => res?.data)
    // Surfaces failures instead of resolving undefined. The sole caller
    // (StaffAthleteTable) already wraps this in try/catch -> setError with a
    // finally that clears the spinner, so a rejection renders as an error
    // rather than an empty athlete list. This was the only swallow in the 12
    // ratchet files that reported NOTHING to Sentry.
    .catch((err) => {
      captureException(err);
      throw err;
    });
};

// Attire/uniform size export — returns { fields, rows } the caller turns
// into a CSV download. Staff-only (manage_athletes) on the backend.
export const exportAttireSizes = (token, includeArchived = false) => {
  const params = new URLSearchParams();
  if (includeArchived) params.append("includeArchived", "true");
  return axios
    .get(`${BASE_URL}/athletes/admin/athletes/attire-export?${params.toString()}`, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => res?.data);
};

// PUT
export const updateAthlete = (token, id, newForm) => {
  return axios
    .put(`${BASE_URL}/athletes/${id}`, newForm, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    // A failed save used to resolve undefined, so the caller's success check
    // simply fell through: no success toast, no error toast, nothing. Both
    // callers (EditAthlete, StaffUpdateAthlete) already catch and toast
    // error.message with a finally that clears loading.
    .catch((err) => {
      captureException(err);
      throw err;
    });
};

// Partial update for auto-save
export const patchAthlete = (token, id, updates) => {
  return axios
    .patch(`${BASE_URL}/athletes/${id}`, updates, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {
      throw err;
    });
};

// Member-safe attire update. Unlike patchAthlete (staff-gated), this hits the
// /attire endpoint that authorizes by membership ownership and limits parents
// to parent_editable fields, so a logged-in parent can save uniform sizes.
export const patchAthleteAttire = (token, id, attire) => {
  return axios
    .patch(
      `${BASE_URL}/athletes/${id}/attire`,
      { attire },
      {
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${token}`,
        },
      }
    )
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {
      throw err;
    });
};

export const getAthleteEnrollment = async (token, athleteId) => {
  return axios
    .get(`${BASE_URL}/athletes/${athleteId}/enrollment`, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {
      captureException(err);
      throw err;
    });
};

// GET Athletes by Birth Month
export const getAthletesByBirthMonth = (token, month, activeOnly = true) => {
  return axios
    .get(`${BASE_URL}/athletes/birthdays`, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
      params: {
        month,
        activeOnly
      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {
      return err?.response?.data;
    });
};

// Upload athlete profile photo
export const uploadAthletePhoto = (token, athleteId, photoFile) => {
  const formData = new FormData();
  formData.append('photo', photoFile);

  return axios
    .post(`${BASE_URL}/athletes/${athleteId}/photo`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => res?.data)
    .catch((err) => {
      return err?.response?.data;
    });
};

// Delete athlete profile photo
export const deleteAthletePhoto = (token, athleteId) => {
  return axios
    .delete(`${BASE_URL}/athletes/${athleteId}/photo`, {
      headers: {
        'Content-Type': 'application/json',
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => res?.data)
    .catch((err) => {
      return err?.response?.data;
    });
};

// Get athlete photo history
export const getAthletePhotoHistory = (token, athleteId) => {
  return axios
    .get(`${BASE_URL}/athletes/${athleteId}/photo-history`, {
      headers: {
        'Content-Type': 'application/json',
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => res?.data)
    .catch((err) => {
      return err?.response?.data;
    });
};

// ============================================
// ARCHIVE MANAGEMENT
// ============================================

// ---------------------------------------------------------------------------
// Danger Zone — handled exactly like the membership one (membershipAPI:
// getMembershipDeleteImpact / hardDeleteMembership / mergeDuplicateMembership).
// All three are gated on the backend by the owner-only hard_delete_athletes
// permission; a 403 means "ask the tenant owner", not "retry".
// ---------------------------------------------------------------------------

// What a hard delete would destroy — real pre-delete counts (active class
// enrollments with their program names, attendance, team assignments), so
// the confirm modal shows numbers, not categories.
export const getAthleteDeleteImpact = (token, athleteId) => {
  return axios
    .get(`${BASE_URL}/athletes/${athleteId}/delete-impact`, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => res?.data)
    .catch((err) => {
      throw err;
    });
};

// Hard-delete an athlete and everything the FK cascade takes with it.
// confirm=true is the API's own "I have seen the impact" gate (without it the
// backend answers 409 + impact instead of deleting); the typed-name modal is
// what earns it. Returns { impact } on success.
export const hardDeleteAthlete = (token, athleteId, reason) => {
  return axios
    .delete(`${BASE_URL}/athletes/${athleteId}?confirm=true`, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
      data: { reason: reason || null, confirm: true },
    })
    .then((res) => res?.data)
    .catch((err) => {
      throw err;
    });
};

// Merge a duplicate athlete INTO this athlete (keeperId survives, the
// duplicate is removed). dryRun=true previews the merge — moved-record
// counts, active enrollments, collisions, cross-family flag — without
// changing anything.
export const mergeDuplicateAthlete = (token, keeperId, duplicateAthleteId, opts = {}) => {
  const dryRun = opts.dryRun !== false;
  const reason = opts.reason || null;
  return axios
    .post(
      `${BASE_URL}/athletes/${keeperId}/merge-duplicate`,
      {
        duplicate_athlete_id: duplicateAthleteId,
        dry_run: dryRun,
        reason,
      },
      {
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${token}`,
        },
      }
    )
    .then((res) => res?.data)
    .catch((err) => {
      throw err;
    });
};

/**
 * Archive an athlete
 * @param {string} token - Auth token
 * @param {string} athleteId - Athlete ID
 * @param {boolean} confirmUnenroll - If true, confirms unenrollment from all programs
 * @param {string} reason - Optional reason for archiving
 * @returns {Promise<{success: boolean, requiresConfirmation?: boolean, enrollmentCount?: number, message?: string}>}
 */
export const archiveAthlete = (token, athleteId, confirmUnenroll = false, reason = null) => {
  return axios
    .post(
      `${BASE_URL}/athletes/${athleteId}/archive`,
      { confirmUnenroll, reason },
      {
        headers: {
          'Content-Type': 'application/json',
          authorization: `Bearer ${token}`,
        },
      }
    )
    .then((res) => res?.data)
    .catch((err) => {
      return err?.response?.data;
    });
};

/**
 * Unarchive an athlete
 * @param {string} token - Auth token
 * @param {string} athleteId - Athlete ID
 * @param {string} restoreStatus - Optional status to restore to (defaults to status before archive)
 * @returns {Promise<{success: boolean, data?: {status: string}, message?: string}>}
 */
export const unarchiveAthlete = (token, athleteId, restoreStatus = null) => {
  return axios
    .post(
      `${BASE_URL}/athletes/${athleteId}/unarchive`,
      { restoreStatus },
      {
        headers: {
          'Content-Type': 'application/json',
          authorization: `Bearer ${token}`,
        },
      }
    )
    .then((res) => res?.data)
    .catch((err) => {
      return err?.response?.data;
    });
};

// ============================================
// KIOSK PIN MANAGEMENT
// ============================================

/**
 * Get athlete's kiosk PIN (masked by default)
 * @param {string} token - Auth token
 * @param {string} athleteId - Athlete ID
 * @param {boolean} reveal - If true, returns actual PIN instead of masked version
 * @returns {Promise<{success: boolean, error?: string, data?: {has_pin: boolean, pin: string | null, pin_length: number | null}}>}
 *   `pin` is masked ("****") unless `reveal`.
 */
export const getAthletePin = (token, athleteId, reveal = false) => {
  const url = reveal
    ? `${BASE_URL}/athletes/${athleteId}/pin?reveal=true`
    : `${BASE_URL}/athletes/${athleteId}/pin`;

  return axios
    .get(url, {
      headers: {
        'Content-Type': 'application/json',
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => res?.data)
    .catch((err) => {
      return err?.response?.data || { success: false, error: 'Failed to fetch PIN' };
    });
};

/**
 * Generate a new kiosk PIN for an athlete
 * @param {string} token - Auth token
 * @param {string} athleteId - Athlete ID
 * @returns {Promise<{success: boolean, message?: string, error?: string, data?: {pin: string}}>}
 */
export const generateAthletePin = (token, athleteId) => {
  return axios
    .post(
      `${BASE_URL}/athletes/${athleteId}/pin/generate`,
      {},
      {
        headers: {
          'Content-Type': 'application/json',
          authorization: `Bearer ${token}`,
        },
      }
    )
    .then((res) => res?.data)
    .catch((err) => {
      return err?.response?.data || { success: false, error: 'Failed to generate PIN' };
    });
};

/**
 * Reset/regenerate an athlete's kiosk PIN
 * @param {string} token - Auth token
 * @param {string} athleteId - Athlete ID
 * @returns {Promise<{success: boolean, message?: string, error?: string, data?: {pin: string}}>}
 */
export const resetAthletePin = (token, athleteId) => {
  return axios
    .post(
      `${BASE_URL}/athletes/${athleteId}/pin/reset`,
      {},
      {
        headers: {
          'Content-Type': 'application/json',
          authorization: `Bearer ${token}`,
        },
      }
    )
    .then((res) => res?.data)
    .catch((err) => {
      return err?.response?.data || { success: false, error: 'Failed to reset PIN' };
    });
};

/**
 * Delete an athlete's kiosk PIN
 * @param {string} token - Auth token
 * @param {string} athleteId - Athlete ID
 * @returns {Promise<{success: boolean, message?: string, error?: string}>}
 */
export const deleteAthletePin = (token, athleteId) => {
  return axios
    .delete(`${BASE_URL}/athletes/${athleteId}/pin`, {
      headers: {
        'Content-Type': 'application/json',
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => res?.data)
    .catch((err) => {
      return err?.response?.data || { success: false, error: 'Failed to delete PIN' };
    });
};

// POST - Schedule a future cancellation for an athlete
export const scheduleAthleteCancellation = (token, athleteId, { cancellation_date, reason }) => {
  return axios
    .post(
      `${BASE_URL}/athletes/${athleteId}/schedule-cancellation`,
      { cancellation_date, reason },
      {
        headers: {
          'Content-Type': 'application/json',
          authorization: `Bearer ${token}`,
        },
      }
    )
    .then((res) => res?.data)
    .catch((err) => {
      return err?.response?.data;
    });
};

// POST - Cancel a scheduled athlete cancellation
export const cancelScheduledAthleteCancellation = (token, athleteId) => {
  return axios
    .post(
      `${BASE_URL}/athletes/${athleteId}/cancel-scheduled-cancellation`,
      {},
      {
        headers: {
          'Content-Type': 'application/json',
          authorization: `Bearer ${token}`,
        },
      }
    )
    .then((res) => res?.data)
    .catch((err) => {
      return err?.response?.data;
    });
};

// Get athlete's event/camp/clinic registrations
export const getAthleteRegistrations = (token, athleteId) => {
  return axios
    .get(`${BASE_URL}/athletes/${athleteId}/registrations`, {
      headers: {
        'Content-Type': 'application/json',
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => res?.data)
    .catch((err) => {
      return err?.response?.data || { success: false, error: 'Failed to fetch registrations' };
    });
};
